// The Feed Viewer's query as a query string, and back. One module so the two directions cannot
// drift: what serialize writes is exactly what parse reads.
//
// Mirrors features/scoreSpec/listFilterUrl.js, which does the same for the spec list. Not shared
// with it — the two shapes have nothing in common but a date window.
//
// WHAT IS IN IT: the query. Which feed, over what window, narrowed to which companies, at what
// fidelity. That is what the Provider owns, and a link that carries it is a link to a question.
//
// WHAT IS NOT, and why:
//   - the table's filters, sort and aggregate. They are a view OVER a result, they live in
//     useFeedTableView beside the rows they describe, and none of them survives a feed change. A
//     URL carrying them would be describing rows the recipient has not fetched yet.
//   - hidden columns. Per feed, and negotiated against a universe the recipient's session has not
//     learned — `columns` is validated server-side, so a stale name is a 422 on the whole read.
//   - the drill-down path. Each entry holds the full result it was showing, so going back restores
//     a table rather than re-fetching one. That cannot be reconstructed from a URL, and pushing
//     `{ result: null }` to stand in would make stepping back blank the table.
//   - useMock / mockKind. A localhost-only fixture control; a shared link must never open one.

const PARAM_KEYS = {
  feedId: 'feed',
  from: 'from',
  to: 'to',
  companies: 'companies',
  slim: 'slim',
};

// A window bound is a CALENDAR DATE carried as a Date at LOCAL midnight — the contract
// knowableWindow.js states at length, and what react-date-picker hands back. So it is read and
// rebuilt in local terms: `toISOString()` here would shift the date by a day in any negative-offset
// zone, which is the exact bug that contract exists to prevent.
const toIsoDate = (date) => {
  if (!date) return null;
  const local = date instanceof Date ? date : new Date(date);
  if (isNaN(local.getTime())) return null;
  const month = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  return `${local.getFullYear()}-${month}-${day}`;
};

// And back to local midnight. Rejects anything that does not round-trip, so '2026-13-45' — which a
// Date constructor would roll forward into 2027 — is treated as absent rather than as a date nobody
// picked.
const fromIsoDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

// Company ids only. The chips carry a symbol and a name as well, but those come from a lookup this
// side cannot do without a round trip — so a hydrated filter is a list of bare ids, which is exactly
// what typing an id into the box produces today, and CompanyFilter already labels and flags those as
// unverified. An id the recipient cannot see the name of is honest; a name invented for it is not.
const toIdList = (companies) =>
  (companies || [])
    .map((company) => Number(company.company_id))
    // `> 0`, matching the parse side. Number(null) is 0 and Number.isInteger(0) is true, so a chip
    // with no id would otherwise serialise as `companies=0,123` — which the recipient's parse drops,
    // leaving the link and the query it opens quietly disagreeing.
    .filter((id) => Number.isInteger(id) && id > 0)
    .join(',');

const fromIdList = (value) =>
  String(value || '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0)
    // `unverified` is the same flag an id typed straight into the box gets: nothing here can check
    // it against a company, so a mistyped one returns no rows rather than pretending to be a match.
    .map((id) => ({ company_id: id, symbol: null, name: null, unverified: true }));

// Empty values are omitted rather than written as blanks, so an untouched form leaves a bare URL and
// a shared link carries only what was actually chosen.
export const serializeFeedQuery = (formState = {}) => {
  const params = new URLSearchParams();
  if (formState.feedId) params.set(PARAM_KEYS.feedId, formState.feedId);

  const from = toIsoDate(formState.fromDate);
  const to = toIsoDate(formState.toDate);
  if (from) params.set(PARAM_KEYS.from, from);
  if (to) params.set(PARAM_KEYS.to, to);

  const ids = toIdList(formState.companies);
  if (ids) params.set(PARAM_KEYS.companies, ids);

  // Written only when true: score-only is the deliberate choice, and the default needs no saying.
  if (formState.slim) params.set(PARAM_KEYS.slim, '1');

  return params;
};

// Takes a query string (with or without the '?'). Unknown keys are ignored and unparseable values
// fall back to the default for that key rather than emptying the query.
//
// Returns a PATCH rather than a whole form state, so a caller merges it over its own defaults and a
// URL that says nothing changes nothing. That is what keeps hydration a no-op on an empty query
// string — without it, 112 page tests would open on a form somebody else decided.
export const parseFeedQuery = (search) => {
  const params = new URLSearchParams(search || '');
  const patch = {};

  const feedId = params.get(PARAM_KEYS.feedId);
  if (feedId) patch.feedId = feedId;

  const from = fromIsoDate(params.get(PARAM_KEYS.from));
  const to = fromIsoDate(params.get(PARAM_KEYS.to));
  if (from) patch.fromDate = from;
  if (to) patch.toDate = to;

  const companies = fromIdList(params.get(PARAM_KEYS.companies));
  if (companies.length) patch.companies = companies;

  if (params.get(PARAM_KEYS.slim) === '1') patch.slim = true;

  return patch;
};
